import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  FormControlLabel, Switch, Typography,
} from '@mui/material';
import {
  EmailOutlined as EmailIcon,
  LockOutlined as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WorkOutline as RoleIcon,
  BadgeOutlined as BadgeIcon,
  BusinessOutlined as ClientIcon,
  StorefrontOutlined as StoreIcon,
  NotificationsOutlined as NotifIcon,
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useAuth from '../../Context/Hooks/useCustomAuth';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { userAPI } from '../../API/userAPI';

import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';

const GATEWAY_ADMIN_CATEGORIES = ['INGESTION_ERRORS', 'DEVICE_ERRORS', 'APPLICATION_ERRORS'];
const CLIENT_ADMIN_CATEGORIES = ['DEVICE_ERRORS'];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const InfoRow = ({ icon, label, value, loading = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <LoadingSkeleton loading={loading} width={140} height={20}>
        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
          {value || '—'}
        </Typography>
      </LoadingSkeleton>
    </Box>
  </Box>
);

const UserProfilePage = () => {
  const { user } = useAuth();
  const { clients, stores } = useGatewayContagem();
  const { t } = useTranslation('users');
  const theme = useTheme();
  const avatarBg = theme.palette.brand?.avatarBg ?? '#5B82A8';

  const isGatewayAdmin = user?.roles?.includes('GATEWAY_ADMIN') || user?.role === 'GATEWAY_ADMIN';
  const isClientAdmin = user?.roles?.includes('CLIENT_ADMIN') || user?.role === 'CLIENT_ADMIN';
  const notifCategories = isGatewayAdmin
    ? GATEWAY_ADMIN_CATEGORIES
    : isClientAdmin
      ? CLIENT_ADMIN_CATEGORIES
      : [];

  const [prefs, setPrefs] = useState({});
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  const loadPrefs = useCallback(async () => {
    if (notifCategories.length === 0) return;
    setPrefsLoading(true);
    try {
      const res = await userAPI.getNotificationPreferences();
      const map = {};
      notifCategories.forEach((cat) => { map[cat] = false; });
      if (!res?.error) {
        (res?.value ?? []).forEach((p) => { map[p.category] = !!p.isEnabled; });
      }
      setPrefs(map);
    } finally {
      setPrefsLoading(false);
    }
  }, [notifCategories.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const handlePrefChange = async (category, enabled) => {
    const previous = prefs[category];
    setPrefs((prev) => ({ ...prev, [category]: enabled }));
    setPrefsSaving(true);
    try {
      const payload = Object.entries({ ...prefs, [category]: enabled })
        .map(([cat, isEnabled]) => ({ category: cat, isEnabled }));
      const res = await userAPI.updateNotificationPreferences(payload);
      if (res?.error) {
        setPrefs((prev) => ({ ...prev, [category]: previous }));
        toast.error(res.description ?? t('notificationPreferences.saveError'));
      }
    } catch {
      setPrefs((prev) => ({ ...prev, [category]: previous }));
      toast.error(t('notificationPreferences.saveError'));
    } finally {
      setPrefsSaving(false);
    }
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});

  const handlePwClose = () => {
    setPwOpen(false);
    setPwForm({ current: '', newPw: '', confirm: '' });
    setPwErrors({});
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const validatePw = () => {
    const errs = {};
    if (!pwForm.current) errs.current = t('profileDialog.requiredCurrent', 'Palavra-passe atual é obrigatória.');
    if (!pwForm.newPw) errs.newPw = t('profileDialog.requiredNew', 'Nova palavra-passe é obrigatória.');
    else if (pwForm.newPw.length < 8) errs.newPw = t('profileDialog.minChars', 'Mínimo 8 caracteres.');
    else if (!/[A-Z]/.test(pwForm.newPw)) errs.newPw = t('profileDialog.uppercaseChar', 'Deve conter pelo menos uma letra maiúscula.');
    else if (!/[^a-zA-Z0-9]/.test(pwForm.newPw)) errs.newPw = t('profileDialog.specialChar', 'Deve conter pelo menos um carácter especial.');
    if (!pwForm.confirm) errs.confirm = t('profileDialog.requiredConfirm', 'Confirmação é obrigatória.');
    else if (pwForm.newPw !== pwForm.confirm) errs.confirm = t('profileDialog.mismatch', 'As palavras-passe não coincidem.');
    return errs;
  };

  const handlePwSubmit = async () => {
    const errs = validatePw();
    if (Object.keys(errs).length > 0) { setPwErrors(errs); return; }

    setPwLoading(true);
    try {
      const result = await userAPI.changePassword(pwForm.current, pwForm.newPw);
      if (!result?.error) {
        toast.success(t('profileDialog.passwordChanged', 'Palavra-passe alterada com sucesso.'));
        handlePwClose();
      } else {
        toast.error(result.description ?? t('profileDialog.passwordChangeError', 'Erro ao alterar palavra-passe.'));
      }
    } catch {
      toast.error(t('profileDialog.passwordChangeError', 'Erro ao alterar palavra-passe.'));
    } finally {
      setPwLoading(false);
    }
  };

  if (!user) return null;

  const roles = Array.isArray(user.roles) && user.roles.length > 0
    ? user.roles
    : user.role
      ? [user.role]
      : [];

  const userClient = user.clientId ? clients.find(c => c.id === user.clientId) : null;
  const userStores = user.storeIds?.length > 0
    ? stores.filter(s => user.storeIds.includes(s.id))
    : [];

  return (
    <Container maxWidth="sm" sx={{ py: 5 }}>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>

        {/* Change password dialog */}
        <Dialog open={pwOpen} onClose={handlePwClose} maxWidth="xs" fullWidth>
          <DialogTitle>{t('profileDialog.changePassword', 'Alterar Palavra-passe')}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField
              label={t('profileDialog.currentPassword', 'Palavra-passe atual')}
              type={showCurrent ? 'text' : 'password'}
              size="small"
              fullWidth
              value={pwForm.current}
              onChange={(e) => setPwForm(f => ({ ...f, current: e.target.value }))}
              error={!!pwErrors.current}
              helperText={pwErrors.current}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowCurrent(v => !v)} edge="end">
                      {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('profileDialog.newPassword', 'Nova palavra-passe')}
              type={showNew ? 'text' : 'password'}
              size="small"
              fullWidth
              value={pwForm.newPw}
              onChange={(e) => setPwForm(f => ({ ...f, newPw: e.target.value }))}
              error={!!pwErrors.newPw}
              helperText={pwErrors.newPw || t('profileDialog.helperNew', 'Mínimo 8 caracteres, uma maiúscula e um carácter especial.')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('profileDialog.confirmPassword', 'Confirmar nova palavra-passe')}
              type={showConfirm ? 'text' : 'password'}
              size="small"
              fullWidth
              value={pwForm.confirm}
              onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              error={!!pwErrors.confirm}
              helperText={pwErrors.confirm}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm(v => !v)} edge="end">
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handlePwClose} disabled={pwLoading}>{t('actions.cancel', 'Cancelar')}</Button>
            <Button
              variant="contained"
              onClick={handlePwSubmit}
              disabled={pwLoading}
              startIcon={pwLoading ? <CircularProgress size={16} /> : null}
            >
              {t('profileDialog.changePassword', 'Alterar')}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Cover */}
        <Box
          sx={{
            height: 80,
            background: `linear-gradient(135deg, ${avatarBg} 0%, ${theme.palette.primary.main} 100%)`,
          }}
        />

        {/* Avatar + name block */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: avatarBg,
              fontSize: '1.5rem',
              fontWeight: 700,
              border: '3px solid',
              borderColor: 'background.paper',
              mt: '-36px',
              mb: 1.5,
            }}
          >
            {getInitials(user.name)}
          </Avatar>

          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {user.name}
          </Typography>
          {user.jobTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {user.jobTitle}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Info rows */}
        <Box sx={{ px: 3, py: 2 }}>
          <InfoRow
            icon={<EmailIcon fontSize="small" />}
            label="Email"
            value={user.email}
          />
          <InfoRow
            icon={<RoleIcon fontSize="small" />}
            label={t('profileDialog.jobTitle', 'Função')}
            value={user.jobTitle}
          />
          <Box sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LockIcon fontSize="small" />}
              onClick={() => setPwOpen(true)}
            >
              {t('profileDialog.changePassword', 'Alterar Palavra-passe')}
            </Button>
          </Box>
        </Box>

        {roles.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <BadgeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('profileDialog.roles', 'Roles de Acesso')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {userClient && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <InfoRow
                icon={<ClientIcon fontSize="small" />}
                label={t('profileDialog.client', 'Cliente')}
                value={userClient.name}
              />
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <StoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('profileDialog.stores', 'Lojas')}
                </Typography>
              </Box>
              {userStores.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {userStores.map((store) => (
                    <Chip
                      key={store.id}
                      label={store.name}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('profileDialog.allStores', 'Todas as lojas')}
                </Typography>
              )}
            </Box>
          </>
        )}
        {notifCategories.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotifIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('notificationPreferences.title')}
                </Typography>
                {prefsSaving && (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                )}
              </Box>
              {prefsLoading ? (
                <LoadingSkeleton loading count={notifCategories.length} preset="switchRow" sx={{ mb: 1 }} />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {notifCategories.map((cat) => (
                    <FormControlLabel
                      key={cat}
                      control={(
                        <Switch
                          size="small"
                          checked={!!prefs[cat]}
                          onChange={(e) => handlePrefChange(cat, e.target.checked)}
                          disabled={prefsSaving}
                        />
                      )}
                      label={(
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {t(`notificationPreferences.categories.${cat}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(`notificationPreferences.descriptions.${cat}`)}
                          </Typography>
                        </Box>
                      )}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Card>
    </Container>
  );
};

export default UserProfilePage;
